(function () {
  'use strict';

  function normalizeId(id) {
    return String(id || '')
      .replace(/#/g, '')
      .trim();
  }

  function findTarget(id) {
    if (!id) return null;
    const escaped = id.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    return (
      document.querySelector('[data-anchor-id="' + escaped + '"]') ||
      document.querySelector('[data-collection-name="' + escaped + '"]')
    );
  }

  function pad(value) {
    return String(Math.max(0, value)).padStart(2, '0');
  }

  function startCountdowns(root) {
    const nodes = Array.from(root.querySelectorAll('[data-ssa-countdown]'));
    if (!nodes.length) return 0;

    const tick = () => {
      const now = Date.now();
      nodes.forEach((node) => {
        const end = Number(node.getAttribute('data-end')) * 1000;
        if (!Number.isFinite(end)) return;
        const remaining = Math.max(0, end - now);
        const days = node.querySelector('[data-days]');
        const hours = node.querySelector('[data-hours]');
        const minutes = node.querySelector('[data-minutes]');
        const seconds = node.querySelector('[data-seconds]');
        if (days) days.textContent = pad(Math.floor(remaining / 86400000));
        if (hours) hours.textContent = pad(Math.floor((remaining % 86400000) / 3600000));
        if (minutes) minutes.textContent = pad(Math.floor((remaining % 3600000) / 60000));
        if (seconds) seconds.textContent = pad(Math.floor((remaining % 60000) / 1000));
      });
    };

    tick();
    return window.setInterval(tick, 1000);
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  var FOCUS_LINE = 200;

  function boxTop(target) {
    const section = target.closest('.shopify-section') || target;
    return window.scrollY + section.getBoundingClientRect().top;
  }

  function scrollTopForLine(docTop, line) {
    return Math.max(0, docTop - line);
  }

  function activeId(entries, probe) {
    let current = entries.length ? entries[0].id : '';
    entries.forEach((entry) => {
      if (probe >= entry.top - 1) current = entry.id;
    });
    return current;
  }

  function assertScrollLine() {
    const chrome = 116;
    const line = chrome + FOCUS_LINE;
    const y = scrollTopForLine(2000, line);
    const probe = y + line;
    const id = activeId(
      [
        { id: 'short', top: 2000 },
        { id: 'next', top: 2400 }
      ],
      probe
    );
    if (y !== 2000 - line || probe !== 2000 || 2000 - (y + chrome) !== FOCUS_LINE || id !== 'short') {
      throw new Error('ssa line');
    }
  }

  class ScrollSpyAnchor extends HTMLElement {
    connectedCallback() {
      this.links = Array.from(this.querySelectorAll('[data-ssa-link]'));
      this.ctas = Array.from(this.querySelectorAll('[data-ssa-cta]'));
      this.wrap = this.querySelector('[data-ssa-wrap]');
      this.list = this.querySelector('[data-ssa-list]');
      this.lockId = '';
      this.lockToken = 0;
      this.acceptEnd = false;
      this.shownId = '';
      this.lockTimer = 0;
      this.countdownTimer = 0;
      this.onClick = this.handleClick.bind(this);
      this.onScroll = this.updateActive.bind(this);
      this.onOverflow = this.updateOverflow.bind(this);
      this.onScrollEnd = this.handleScrollEnd.bind(this);

      this.links.forEach((link) => link.addEventListener('click', this.onClick));
      this.ctas.forEach((cta) => cta.addEventListener('click', this.onClick));
      this.countdownTimer = startCountdowns(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('scrollend', this.onScrollEnd);
      if (this.list) this.list.addEventListener('scroll', this.onOverflow, { passive: true });
      window.addEventListener('resize', this.onOverflow);

      this.updateActive();
      this.updateOverflow();
    }

    disconnectedCallback() {
      this.links.forEach((link) => link.removeEventListener('click', this.onClick));
      this.ctas.forEach((cta) => cta.removeEventListener('click', this.onClick));
      if (this.countdownTimer) window.clearInterval(this.countdownTimer);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('scrollend', this.onScrollEnd);
      window.removeEventListener('resize', this.onOverflow);
      if (this.list) this.list.removeEventListener('scroll', this.onOverflow);
      if (this.lockTimer) window.clearTimeout(this.lockTimer);
    }

    focusLine() {
      const nav = this.closest('.shopify-section');
      const barHeight = nav ? nav.offsetHeight : 0;
      const raw = getComputedStyle(document.body).getPropertyValue('--header-height').trim();
      const header = parseFloat(raw);
      const headerHeight = Number.isFinite(header) ? header : 0;
      return headerHeight + barHeight + FOCUS_LINE;
    }

    handleClick(event) {
      const link = event.currentTarget;
      const firstId = normalizeId((link.getAttribute('data-target') || '').split(',')[0]);
      const target = findTarget(firstId);
      if (!target) return;

      event.preventDefault();
      const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
      this.lockTo(firstId);
      const top = scrollTopForLine(boxTop(target), this.focusLine());
      window.scrollTo({ top: top, behavior: behavior });
      if (behavior === 'auto') this.releaseLock();
    }

    lockTo(id) {
      const token = ++this.lockToken;
      this.lockId = id;
      this.acceptEnd = false;
      this.shownId = '';
      this.setActive(id);
      if (this.lockTimer) window.clearTimeout(this.lockTimer);
      // ponytail: scrollend is the real release; this only covers browsers that never fire it
      this.lockTimer = window.setTimeout(() => {
        if (this.lockToken === token) this.releaseLock();
      }, 4000);
      requestAnimationFrame(() => {
        if (this.lockToken === token) this.acceptEnd = true;
      });
    }

    handleScrollEnd(event) {
      if (!this.lockId || !this.acceptEnd) return;
      const target = event.target;
      if (target && target !== document && target !== document.scrollingElement) return;
      this.releaseLock();
    }

    releaseLock() {
      this.lockId = '';
      this.acceptEnd = false;
      if (this.lockTimer) {
        window.clearTimeout(this.lockTimer);
        this.lockTimer = 0;
      }
      this.updateActive();
    }

    updateActive() {
      if (this.lockId) return;

      const probe = window.scrollY + this.focusLine();
      const entries = [];

      this.links.forEach((link) => {
        (link.getAttribute('data-target') || '').split(',').forEach((raw) => {
          const id = normalizeId(raw);
          const target = findTarget(id);
          if (!target) return;
          entries.push({ id, top: boxTop(target) });
        });
      });

      entries.sort((a, b) => a.top - b.top);
      this.setActive(activeId(entries, probe));
    }

    setActive(current) {
      let activeLink = null;
      this.links.forEach((link) => {
        const ids = (link.getAttribute('data-target') || '').split(',').map(normalizeId);
        const active = current !== '' && ids.indexOf(current) !== -1;
        link.classList.toggle('is-active', active);
        link.classList.toggle('active', active);
        if (active) {
          activeLink = link;
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });
      if (current === this.shownId) return;
      this.shownId = current;
      this.revealActive(activeLink);
    }

    revealActive(link) {
      if (!this.list || !link) return;
      const item = link.closest('li') || link;
      const list = this.list;
      const fade = 24;
      const listRect = list.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const viewLeft = listRect.left + fade;
      const viewRight = listRect.right - fade;
      if (itemRect.left >= viewLeft && itemRect.right <= viewRight) return;
      const delta = itemRect.left - listRect.left - (list.clientWidth - itemRect.width) / 2;
      list.scrollTo({
        left: Math.max(0, list.scrollLeft + delta),
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    updateOverflow() {
      if (!this.wrap || !this.list) return;
      const max = this.list.scrollWidth - this.list.clientWidth;
      this.wrap.classList.toggle('is-overflow-start', this.list.scrollLeft > 4);
      this.wrap.classList.toggle('is-overflow-end', this.list.scrollLeft < max - 4);
    }
  }

  assertScrollLine();

  if (!customElements.get('scroll-spy-anchor')) {
    customElements.define('scroll-spy-anchor', ScrollSpyAnchor);
  }
})();
